import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Calendar } from 'react-date-range';
import * as dateRangeLocales from 'react-date-range/dist/locale';
import { DateTime } from 'luxon';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

export interface LomavisDateInput1Props {
  id: string;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  minDate?: Date;
  disabled?: boolean;
  placeholder?: string;
}

export const parseLocalDateValue = (value: string) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

export const formatLocalDateValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;

const getHtmlLang = () => {
  if (typeof document === 'undefined') return 'de';
  const lang = document.getElementsByTagName('html')[0]?.getAttribute('lang');
  if (!lang) return 'de';
  return lang === 'en-us' ? 'en' : lang;
};

export const LomavisDateInput1: React.FC<LomavisDateInput1Props> = ({
  id,
  label,
  value,
  onChange,
  minDate,
  disabled = false,
  placeholder
}) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const selectedDate = useMemo(() => parseLocalDateValue(value), [value]);
  const calendarDate = selectedDate || minDate || new Date();
  const lang = getHtmlLang();
  const localeMap = dateRangeLocales as Record<string, unknown>;
  const calendarLocale = localeMap[lang] || localeMap.enUS;
  const displayValue = selectedDate
    ? DateTime.fromJSDate(selectedDate)
        .setLocale(lang)
        .toLocaleString(DateTime.DATE_SHORT)
    : '';
  const resolvedPlaceholder =
    placeholder ??
    DateTime.fromObject({ year: 2000, month: 1, day: 1 })
      .setLocale(lang)
      .toLocaleParts(DateTime.DATE_SHORT)
      .map((part) => {
        if (part.type === 'day') return 'DD';
        if (part.type === 'month') return 'MM';
        if (part.type === 'year') return 'YYYY';
        return part.value;
      })
      .join('');

  const openCalendar = () => {
    if (!disabled) {
      setShowCalendar(true);
    }
  };

  useEffect(() => {
    if (!showCalendar) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (targetRef.current?.contains(target)) {
        return;
      }

      setShowCalendar(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [showCalendar]);

  return (
    <div>
      <Form.Label
        className="d-block mt-1 text-muted small fw-semibold"
        htmlFor={id}
      >
        {label}
      </Form.Label>
      <div
        ref={targetRef}
        className="position-relative"
        onMouseDown={() => {
          openCalendar();
        }}
      >
        <Form.Control
          id={id}
          type="text"
          inputMode="none"
          autoComplete="off"
          readOnly
          disabled={disabled}
          placeholder={resolvedPlaceholder}
          value={displayValue}
          onClick={openCalendar}
          onFocus={openCalendar}
        />
        <span
          className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
          style={{ pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <i className="fa-regular fa-calendar-days" />
        </span>
        {showCalendar && (
          <div
            className="datePickerpopover position-absolute start-0 bg-white border rounded shadow"
            style={{
              top: 'calc(100% + 4px)',
              zIndex: 1060
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Calendar
              date={calendarDate}
              minDate={minDate}
              locale={calendarLocale}
              onChange={(date: Date) => {
                onChange(formatLocalDateValue(date));
                setShowCalendar(false);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
